import React, { useCallback, useEffect, useState } from "react";
import { Building2, Loader2 } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Modal from "../Modal/Modal";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./StorageModals.module.scss";

const formatGB = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  return `${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(value))} GB`;
};

const formatDateTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ViewStorageModal = ({ open, orgId, onClose, onError }) => {
  const [loading, setLoading] = useState(false);
  const [storage, setStorage] = useState(null);

  const fetchStorage = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await apiGet("catalog", `/billing/storage/orgs/${orgId}`);
      if (res?.status !== "success") {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load storage details",
          }),
        );
      }
      setStorage(res.data || null);
    } catch (err) {
      if (typeof onError === "function") {
        onError(getErrorMessage(err, "Failed to load storage details"));
      }
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [orgId, onError, onClose]);

  useEffect(() => {
    if (open) {
      setStorage(null);
      fetchStorage();
    }
  }, [open, fetchStorage]);

  if (!open) return null;

  const percent = Math.max(0, Math.min(100, Number(storage?.percentUsed ?? 0)));
  const trackTone =
    percent >= 90 ? "danger" : percent >= 70 ? "warning" : "primary";

  const fields = storage
    ? [
        { label: "Organization", value: storage.name },
        { label: "Company", value: storage.companyName },
        { label: "Total Limit", value: formatGB(storage.storageLimitGb) },
        { label: "Used", value: formatGB(storage.storageUsedGb) },
        {
          label: "Available",
          value: formatGB(
            Number(storage.storageAvailableBytes) / 1_000_000_000,
          ),
        },
        { label: "Percent Used", value: `${formatNumber(percent)}%` },
        { label: "Org ID", value: storage.orgId },
        { label: "Last Updated", value: formatDateTime(storage.updatedAt) },
      ]
    : [];

  return (
    <Modal title="Storage Details" onClose={onClose}>
      {loading ? (
        <div className={styles["storage-modal__loading"]}>
          <Loader2 className={styles["storage-modal__spin"]} size={28} />
          <span>Loading storage details…</span>
        </div>
      ) : storage ? (
        <div className={styles["storage-modal__container"]}>
          <div className={styles["storage-modal__section"]}>
            <div className={styles["storage-modal__section-header"]}>
              <div className={styles["storage-modal__icon-wrapper"]}>
                <Building2 size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["storage-modal__section-title"]}>
                {storage.name || storage.companyName || "Organization"}
              </h3>
            </div>

            <div className={styles["storage-modal__progress-block"]}>
              <div className={styles["storage-modal__progress-head"]}>
                <span>Storage Used</span>
                <strong>{formatNumber(percent)}%</strong>
              </div>
              <div
                className={`${styles["storage-modal__progress"]} ${
                  styles[`storage-modal__progress--${trackTone}`]
                }`}
              >
                <div
                  className={styles["storage-modal__progress-fill"]}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>

            <div className={styles["storage-modal__grid"]}>
              {fields.map((field, idx) => (
                <div key={idx} className={styles["storage-modal__field"]}>
                  <span className={styles["storage-modal__field-label"]}>
                    {field.label}
                  </span>
                  <span className={styles["storage-modal__field-value"]}>
                    {field.value || field.value === 0
                      ? String(field.value)
                      : "--"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(value));
};

export default ViewStorageModal;
